import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { money } from './utils';

const EDIT_FIELDS = [
  { key: 'insurance_expiry_date', label: 'Sigorta bitiş' },
  { key: 'inspection_expiry_date', label: 'Muayene bitiş' },
  { key: 'next_maintenance_date', label: 'Sonraki bakım' },
];
const RENTAL_EDIT = [{ key: 'rental_end_date', label: 'Kiralık dönüş tarihi' }];
const isIso = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

export function RowEditor({ row, onSaved }) {
  const isRental = row.ownership_type === 'Kiralık';
  const fields = isRental ? [...EDIT_FIELDS, ...RENTAL_EDIT] : EDIT_FIELDS;
  const [vals, setVals] = useState(Object.fromEntries(fields.map((f) => [f.key, row[f.key] || ''])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(vals).filter(([k, v]) => v && v !== (row[k] || '')));
    if (Object.values(payload).some((v) => !isIso(v))) return toast.error('Geçersiz tarih');
    if (!Object.keys(payload).length) return toast.info('Değişiklik yok');
    setSaving(true);
    try { await api.put(`/vehicles/${row.id}`, payload); toast.success('Kaydedildi'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 space-y-4" data-testid={`vehicle-row-editor-${row.id}`}>
      <div className="flex flex-wrap items-end gap-3">
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1 text-xs text-slate-500">
            {f.label}
            <Input type="date" value={vals[f.key]} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
              className="h-8 w-40" data-testid={`vehicle-edit-${f.key}-${row.id}`} />
          </label>
        ))}
        <Button size="sm" onClick={save} disabled={saving} data-testid={`vehicle-edit-save-${row.id}`}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Kaydet</Button>
        <Link to={`/dashboard/vehicles/${row.id}`} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid={`vehicle-open-detail-${row.id}`}>
          Araç ayrıntısı <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      {isRental && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid={`vehicle-rental-info-${row.id}`}>
          <div><p className="text-slate-500">Yerine geldiği araç</p><p className="font-semibold font-mono">{row.replaces_plate || '—'}</p></div>
          <div><p className="text-slate-500">Kiralama firması</p><p className="font-semibold">{row.rental_company || '—'}</p></div>
          <div><p className="text-slate-500">Başlangıç</p><p className="font-semibold">{row.rental_start_date || '—'}</p></div>
          <div><p className="text-slate-500">Aylık ücret</p><p className="font-semibold">{money(row.rental_monthly_fee)}</p></div>
          <div><p className="text-slate-500">Günlük ücret</p><p className="font-semibold">{money(row.rental_daily_fee)}</p></div>
        </div>
      )}
      {(row.missing_fields.length > 0 || row.missing_items.length > 0) && (
        <p className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold">Eksikler:</span> {[...row.missing_items, ...row.missing_fields].join(', ')}
          <span className="text-slate-400"> — diğer bilgileri araç ayrıntısından doldurabilirsiniz.</span>
        </p>
      )}
    </div>
  );
}
